// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useSports } from "@/hooks/useSports";
import type { NcaaCalendarEvent } from "./AdminNcaaCalendarModule";

interface AdminNcaaCalendarFormProps {
  event?: NcaaCalendarEvent | null;
  onEventSaved: () => void;
  onClose: () => void;
}

interface FormData {
  title: string;
  start_date: string;
  end_date: string;
  category: string;
  division: string;
  description: string;
  sport_id: string;
}

const EVENT_CATEGORIES = [
  { value: 'ncaa_contact', label: 'NCAA Contact Period' },
  { value: 'ncaa_dead', label: 'NCAA Dead Period' },
  { value: 'ncaa_evaluation', label: 'NCAA Evaluation Period' },
  { value: 'ncaa_quiet', label: 'NCAA Quiet Period' },
  { value: 'fafsa', label: 'FAFSA Deadline' },
  { value: 'testing', label: 'Testing Date' },
  { value: 'other', label: 'Other Event' },
];

const NCAA_DIVISIONS = [
  { value: 'All', label: 'All Divisions' },
  { value: 'D1', label: 'Division I' },
  { value: 'D2', label: 'Division II' },
  { value: 'D3', label: 'Division III' },
  { value: 'NAIA', label: 'NAIA' },
  { value: 'NJCAA', label: 'NJCAA' },
];

const AdminNcaaCalendarForm: React.FC<AdminNcaaCalendarFormProps> = ({
  event,
  onEventSaved,
  onClose,
}) => {
  const [formData, setFormData] = useState<FormData>({
    title: "",
    start_date: "",
    end_date: "",
    category: "",
    division: "",
    description: "",
    sport_id: "",
  });
  
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { sports, isLoading: sportsLoading } = useSports();

  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title || "",
        start_date: event.start_date || "",
        end_date: event.end_date || "",
        category: event.category || "",
        division: event.division || "",
        description: event.description || "",
        sport_id: (event as any).sport_id || "",
      });
    }
  }, [event]);

  const getSourceFromCategory = (category: string): string => {
    if (category.startsWith('ncaa_')) {
      return 'ncaa';
    } else if (category === 'fafsa') {
      return 'fafsa';
    } else if (category === 'testing') {
      return 'testing';
    } else {
      return 'custom';
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.title?.trim()) {
      errors.title = 'Event title is required';
    }

    if (!formData.start_date) {
      errors.start_date = 'Start date is required';
    }

    if (!formData.category) {
      errors.category = 'Category is required';
    }

    if (formData.end_date && formData.start_date && formData.end_date < formData.start_date) {
      errors.end_date = 'End date cannot be before start date';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Dynamically determine source and is_custom based on category
      const source = getSourceFromCategory(formData.category);
      const isCustom = source === 'custom';
      
      // Prepare event data with proper null handling for RLS
      const eventData = {
        title: formData.title.trim(),
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        category: formData.category,
        division: formData.division === 'All' ? null : formData.division || null,
        description: formData.description.trim() || null,
        source: source,
        is_custom: isCustom,
        profile_id: null, // Admin events are not profile-specific
        sport_id: formData.sport_id || null,
        related_entity_id: null,
      };

      console.log("🏈 Calendar Admin: Submitting event data:", eventData);

      if (event) {
        // Update existing event
        console.log("🏈 Calendar Admin: Updating event:", event.id);
        
        const { error } = await supabase
          .from('calendar_events')
          .update(eventData)
          .eq('id', event.id);
          
        if (error) {
          console.error("🏈 Calendar Admin: Update error:", error);
          throw error;
        }
        
        toast({
          title: 'Success',
          description: 'Event updated successfully'
        });
      } else {
        // Create new event
        console.log("🏈 Calendar Admin: Creating new event");
        
        const { error } = await supabase
          .from('calendar_events')
          .insert([eventData]);
          
        if (error) {
          console.error("🏈 Calendar Admin: Insert error:", error);
          throw error;
        }
        
        toast({
          title: 'Success',
          description: 'Event created successfully'
        });
      }
      
      onEventSaved();
      
    } catch (error) {
      console.error('🏈 Calendar Admin: Error saving event:', error);
      toast({
        title: 'Error',
        description: `Failed to save event: ${error.message || 'Unknown error'}`,
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label htmlFor="title">Event Title *</Label>
          <Input
            id="title"
            value={formData.title}
            onChange={(e) => setFormData({...formData, title: e.target.value})}
            placeholder="Enter event title"
            className={formErrors.title ? "border-red-500" : ""}
          />
          {formErrors.title && (
            <p className="text-sm text-red-500 mt-1">{formErrors.title}</p>
          )}
        </div>
        
        <div>
          <Label htmlFor="category">Category *</Label>
          <Select 
            value={formData.category} 
            onValueChange={(value) => setFormData({...formData, category: value})}
          >
            <SelectTrigger className={formErrors.category ? "border-red-500" : ""}>
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {EVENT_CATEGORIES.map(category => (
                <SelectItem key={category.value} value={category.value}>
                  {category.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {formErrors.category && (
            <p className="text-sm text-red-500 mt-1">{formErrors.category}</p>
          )}
        </div>

        <div>
          <Label htmlFor="division">Division</Label>
          <Select 
            value={formData.division} 
            onValueChange={(value) => setFormData({...formData, division: value})}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select division" />
            </SelectTrigger>
            <SelectContent>
              {NCAA_DIVISIONS.map(division => (
                <SelectItem key={division.value} value={division.value}>
                  {division.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="sport">Sport (Optional)</Label>
          <Select 
            value={formData.sport_id} 
            onValueChange={(value) => setFormData({...formData, sport_id: value})}
            disabled={sportsLoading}
          >
            <SelectTrigger>
              <SelectValue placeholder={sportsLoading ? "Loading sports..." : "Select sport (optional)"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">No specific sport</SelectItem>
              {sports.map(sport => (
                <SelectItem key={sport.id} value={sport.id}>
                  {sport.name}{sport.gender ? ` - ${sport.gender}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="start_date">Start Date *</Label>
          <Input
            id="start_date"
            type="date"
            value={formData.start_date}
            onChange={(e) => setFormData({...formData, start_date: e.target.value})}
            className={formErrors.start_date ? "border-red-500" : ""}
          />
          {formErrors.start_date && (
            <p className="text-sm text-red-500 mt-1">{formErrors.start_date}</p>
          )}
        </div>

        <div>
          <Label htmlFor="end_date">End Date</Label>
          <Input
            id="end_date"
            type="date"
            value={formData.end_date}
            onChange={(e) => setFormData({...formData, end_date: e.target.value})}
            className={formErrors.end_date ? "border-red-500" : ""}
          />
          {formErrors.end_date && (
            <p className="text-sm text-red-500 mt-1">{formErrors.end_date}</p>
          )}
        </div>
      </div>

      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          placeholder="Enter event description..."
          rows={3}
        />
      </div>
      
      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : (event ? 'Update Event' : 'Create Event')}
        </Button>
      </div>
    </form>
  );
};

export default AdminNcaaCalendarForm;
